import { inject, injectable } from "inversify";
import { AjaxService, FormComponentService, SessionValidationService, UrlService } from ".";
import { IpfModule } from "..";
import { Constants } from "../constants.generated";
import { logInfo } from "../functions/client-log";
import { secondsToString } from "../functions/seconds-to-string";
import { TYPES } from "../ioc/types";
import { ConfirmationOption, ConfirmationPromptOptions, SessionExpiryInfo } from "../models/app";
import { HttpMethods } from "../models/http";
import { ServiceBase } from "./ServiceBase";

/**
 * Session validation service, enables the setup and refresh of a session expiry timer.
 */
@injectable()
export class IpfSessionValidationService extends ServiceBase implements SessionValidationService {

	private static readonly sessionExpiryLsKey = "SessionExpiry";
	private static readonly timerStartLsKey = "SessionTimerStart";
	private static readonly expirySecondsOffset = 5;
	private static readonly disableLogging = true;

	private intervalRef?: number;
	private expirySeconds: number = 0;
	private promptVisible = false;
	private expiryElement: HTMLElement | null = null;
	private tickerElement: HTMLElement | null = null;
	private static promptOptions: ConfirmationPromptOptions | undefined;

	/**
	 * Initialises a new instance of the IpfSessionValidationService class.
	 * @param ajaxService AJAX service, used to make AJAX HTTP requests.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 * @param urlService URL service, used to generate URLs.
	 * @param formComponentService Form component service, used to render React controls.
	 */
	public constructor(
		@inject(TYPES.AjaxService) private ajaxService: AjaxService,
		@inject(TYPES.IpfModule) private ipfModule: IpfModule,
		@inject(TYPES.UrlService) private urlService: UrlService,
		@inject(TYPES.FormComponentService) private formComponentService: FormComponentService
	) {
		super();

		this.sessionExpiry = this.ipfModule.context.sessionExpiry;
	}

	/**
	 * Gets session expiry info from localStorage.
	 */
	private get sessionExpiry() {
		const json = localStorage.getItem(IpfSessionValidationService.sessionExpiryLsKey);
		return json ? JSON.parse(json) as SessionExpiryInfo : this.ipfModule.context.sessionExpiry;
	}

	/**
	 * Sets session expiry info into localStorage. The info is stored as a JSON string.
	 */
	private set sessionExpiry(value: SessionExpiryInfo | undefined) {
		localStorage.setItem(IpfSessionValidationService.sessionExpiryLsKey, JSON.stringify(value));
	}

	/**
	 * Gets the timer start date. Retrieves from localStorage so works across browser tabs.
	 */
	private get timerStart() {
		const json = localStorage.getItem(IpfSessionValidationService.timerStartLsKey);
		return json ? new Date(json) : null;
	}

	/**
	 * Sets the timer start date into localStorage. The info is stored as an ISO date string.
	 */
	private set timerStart(value: Date | null) {

		if (value) {

			localStorage.setItem(IpfSessionValidationService.timerStartLsKey, value.toISOString());
		}
		else {

			localStorage.removeItem(IpfSessionValidationService.timerStartLsKey);
		}
	}

	/**
	 * @inheritdoc
	 */
	setupSessionTimer() {

		let sessionExpiry = this.sessionExpiry;

		if (!sessionExpiry) {

			return;
		}

		this.expiryElement = document.getElementById("session-timer");
		this.tickerElement = document.getElementById("session-time-left");

		if (this.expiryElement && this.tickerElement && sessionExpiry.sessionExpiryWarningSeconds) {

			this.startSessionExpiryTimer();
			this.checkSessionExpiry();
		}
	}

	/**
	 * @inheritdoc
	 */
	refreshSessionTimer() {

		const sessionExpiry = this.sessionExpiry;

		if (sessionExpiry) {

			// convert from minutes to seconds
			const sessionExpirySeconds = sessionExpiry.sessionTimeout * 60;

			// check the absolute session expiry and if it occurs sooner
			if (sessionExpiry.absoluteSessionExpirySeconds && sessionExpirySeconds > sessionExpiry.absoluteSessionExpirySeconds) {

				sessionExpiry.sessionExpirySeconds =  sessionExpiry.absoluteSessionExpirySeconds;
			}
			else {

				sessionExpiry.sessionExpirySeconds = sessionExpirySeconds;
			}

			this.startSessionExpiryTimer();
		}
	}

	private checkSessionExpiry() {

		let sessionExpiry = this.sessionExpiry;

		if (!this.timerStart) {

			this.logUserOut();
			return;
		}

		const timeDiff = new Date().getTime() - this.timerStart.getTime();
		const seconds = this.expirySeconds - Math.ceil(timeDiff / 1000);

		// log seconds until expiry
		this.logExpirySeconds(seconds);

		// if the session expired log the user out
		if (seconds < 1) {

			this.logUserOut();
			return;
		}

		// show the amount of time remaining to the user
		if (this.tickerElement) {

			this.tickerElement.innerText = secondsToString(seconds > 0 ? seconds : 0);
		}

		// show the timer prompt
		if (!this.promptVisible && sessionExpiry && sessionExpiry.sessionExpiryWarningSeconds && seconds <= sessionExpiry.sessionExpiryWarningSeconds) {

			if (!serviceType.promptOptions) {

				serviceType.promptOptions = {
					title: "Session Will Expire Soon",
					content: this.expiryElement,
					closeOnBackdropClick: false,
					confirmOption: {
						text: "Stay Logged On",
						handler: () => {

							// handle the user choosing to extend their session
							this.ajaxService.request<SessionExpiryInfo>({
								url: this.urlService.url("/Session/Refresh"),
								method: HttpMethods.Post
							})
								.then(result => {

									if (result) {
																				
										if (serviceType.promptOptions?.close) {
											serviceType.promptOptions.close();
											this.promptVisible = false;
										}
									}
								});
						}
					} as ConfirmationOption,
					cancelOption: {
						text: "Log Out",
						handler: () => {

							// handle the user choosing to log out
							// call any provided close handler and ensure the prompt content is not visible
							if (serviceType.promptOptions?.close) {
								serviceType.promptOptions.close();
								this.promptVisible = false;
							}

							this.logUserOut();
						}
					} as ConfirmationOption,
					onRendered: (isOpen: boolean) => {

						if (isOpen) {

							// once rendered and open, show the expiry timer
							this.showExpiryTimer(true);
							this.expiryElement?.closest("#dialog-content")?.classList.add("session-expiry-dialog-content");
						}
					}
				} as ConfirmationPromptOptions;

				this.formComponentService.showConfirmationPrompt(serviceType.promptOptions);
			}
			else if (serviceType.promptOptions.open && this.expiryElement) {

				this.showExpiryTimer(false);
			}
		}
		else if (this.promptVisible && (!sessionExpiry?.sessionExpiryWarningSeconds || seconds > sessionExpiry.sessionExpiryWarningSeconds)) {

			this.hideExpiryTimer();
		}
	}

	private getSessionExpirySeconds() {

		const sessionExpiry = this.sessionExpiry;
		let expirySeconds = sessionExpiry?.sessionExpirySeconds ?? Number.MAX_VALUE;

		if (sessionExpiry && sessionExpiry.absoluteSessionExpirySeconds && sessionExpiry.absoluteSessionExpirySeconds < expirySeconds) {

			expirySeconds = sessionExpiry.absoluteSessionExpirySeconds;
		}

		expirySeconds -= IpfSessionValidationService.expirySecondsOffset;

		return expirySeconds;
	}

	private setExpirySeconds() {

		this.expirySeconds = this.getSessionExpirySeconds();

		this.logSetExpirySeconds();
	}

	private startSessionExpiryTimer() {

		this.stopSessionExpiryTimer();
		this.setExpirySeconds();
		this.timerStart = new Date();
		this.intervalRef = window.setInterval(() => this.checkSessionExpiry(), 1000);

		this.logStartSessionExpiryTimer();
	}

	private stopSessionExpiryTimer() {

		clearInterval(this.intervalRef);
	}

	private showExpiryTimer(isFirstime: boolean) {

		if (isFirstime && this.expiryElement) {

			this.expiryElement.style.display = 'block';
		}
		else if (serviceType.promptOptions?.open && this.expiryElement) {

			serviceType.promptOptions.open();
			document.getElementById("dialog-content")?.appendChild(this.expiryElement);
		}

		this.promptVisible = true;
	}

	private hideExpiryTimer() {

		if (this.expiryElement) {

			document.body.appendChild(this.expiryElement);
			this.expiryElement.style.display = 'none';
		}

		if (serviceType.promptOptions?.close) {

			serviceType.promptOptions.close();
		}

		this.promptVisible = false;
	}

	private logExpirySeconds(seconds: number) {

		if (!serviceType.disableLogging) {
			logInfo(`Session expiry in ${seconds} second(s). ${new Date().toISOString()}`);
		}
	}

	private logSetExpirySeconds() {


		if (!serviceType.disableLogging) {
			logInfo(`Session timer set to expire in ${this.expirySeconds} second(s). ${new Date().toISOString()}`);
		}
	}

	private logStartSessionExpiryTimer() {

		if (!serviceType.disableLogging) {
			logInfo(`Session timer started. ${new Date().toISOString()}`);
		}
	}

	private logUserOut() {

		this.stopSessionExpiryTimer();
		this.timerStart = null;

		const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
		window.location.href = this.urlService.url(Constants.LogoutPageName, { returnUrl });
	}
}

const serviceType = IpfSessionValidationService;